import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useBrand } from "@/lib/brand";
import { ArrowRight } from "lucide-react";

const AI_BG = "https://images.pexels.com/photos/12187128/pexels-photo-12187128.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";
const MEDIA_BG = "https://images.unsplash.com/photo-1618556450994-a6a128ef0d9d?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85";

export default function SplitLanding() {
  const { setBrand } = useBrand();
  return (
    <div className="grain relative min-h-screen w-full overflow-hidden">
      <div className="flex flex-col md:flex-row min-h-screen">
        {/* AI Academy half */}
        <SplitHalf
          to="/academy"
          onChoose={() => setBrand("ai")}
          bg={AI_BG}
          accent="#00FFC2"
          eyebrow="Half 01 · Learn"
          title={
            <span className="font-display-ai text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] text-white">
              AI ACADEMY
            </span>
          }
          subtitle="Faceless content systems, viral hooks, AI workflows, monetization. Build the creator OS that scales without you in front of the camera."
          ctaLabel="Enter Academy"
          testid="split-hero-ai-link"
          tilt="left"
        />
        {/* Media half */}
        <SplitHalf
          to="/studio"
          onChoose={() => setBrand("media")}
          bg={MEDIA_BG}
          accent="#A855F7"
          eyebrow="Half 02 · Build"
          title={
            <div className="flex flex-col gap-2">
              <span className="font-display-media italic text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] text-white">
                Edit Bae Studios
              </span>
              <span className="text-[11px] tracking-[0.32em] uppercase text-gray-400">
                The Creative Studio of AI Creator OS
              </span>
            </div>
          }
          subtitle="Cinematic AI commercials, luxury brand campaigns, premium content systems, and high-converting creative assets for modern businesses."
          ctaLabel="Book Strategy Call"
          secondaryLabel="View Portfolio"
          secondaryTo="/studio#work"
          testid="split-hero-media-link"
          tilt="right"
        />
      </div>

      {/* Center divider mark */}
      <div className="hidden md:flex pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 items-center z-20">
        <div className="glass rounded-full px-4 py-2 text-[10px] tracking-[0.32em] uppercase">
          Choose a path
        </div>
      </div>
    </div>
  );
}

function SplitHalf({ to, onChoose, bg, accent, eyebrow, title, subtitle, smallline, ctaLabel, secondaryLabel, secondaryTo, testid, tilt }) {
  const isRight = tilt === "right";
  return (
    <motion.div
      whileHover={{ flex: 1.15 }}
      transition={{ type: "spring", stiffness: 120, damping: 22 }}
      className="relative flex-1 min-h-[58vh] md:min-h-screen overflow-hidden border-r hairline last:border-r-0"
    >
      <div
        className="absolute inset-0 bg-cover bg-center scale-105"
        style={{ backgroundImage: `url(${bg})` }}
      />
      {/* base darken */}
      <div
        className="absolute inset-0"
        style={{
          background: isRight
            ? "linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.75))"
            : "linear-gradient(180deg, rgba(0,0,0,0.55), rgba(0,0,0,0.85))",
        }}
      />

      {/* Edit Bae side: cinematic accents */}
      {isRight && (
        <>
          {/* vertical film-strip glow bar */}
          <div className="pointer-events-none absolute right-8 top-0 bottom-0 w-[3px] opacity-40" style={{
            background: "linear-gradient(180deg, transparent, #A855F7 30%, #A855F7 70%, transparent)",
            boxShadow: "0 0 24px #A855F7",
          }} />
          {/* Reel number — desktop only, positioned below eyebrow line */}
          <div className="pointer-events-none hidden md:flex absolute top-20 right-16 items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-gray-300">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A855F7] animate-pulse shadow-[0_0_12px_#A855F7]" />
            Reel · Live
          </div>
          {/* central purple glow orb */}
          <div className="pointer-events-none absolute top-1/4 right-1/4 w-[520px] h-[520px] rounded-full blur-[120px] opacity-45 float-slow" style={{ background: "radial-gradient(circle, #A855F7 0%, transparent 65%)" }} />
          {/* diagonal noise/grain gradient sweep */}
          <div className="pointer-events-none absolute inset-0" style={{
            background: "linear-gradient(115deg, rgba(168,85,247,0.18) 0%, transparent 45%, rgba(0,0,0,0.5) 100%)",
          }} />
          {/* frame corner ticks */}
          <div className="pointer-events-none absolute bottom-8 left-8 flex items-center gap-4 text-[9px] tracking-[0.4em] uppercase text-gray-400">
            <span>Take 01</span>
            <span className="w-8 h-px bg-gray-600" />
            <span>24 fps</span>
          </div>
        </>
      )}

      {/* accent blob */}
      <div
        className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full blur-3xl opacity-30 float-slow"
        style={{ background: accent }}
      />

      <div className="relative z-10 h-full flex flex-col justify-between p-10 pt-14 md:pt-16 lg:p-16">
        <div className={`label-eyebrow ${isRight ? "md:text-right" : ""}`}>{eyebrow}</div>
        <div className={`${isRight ? "md:text-right md:items-end md:ml-auto" : ""} max-w-xl flex flex-col gap-6`}>
          {typeof title === "string" ? (
            <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95] text-white">{title}</h2>
          ) : (
            <div className={isRight ? "md:ml-auto" : ""}>{title}</div>
          )}
          {smallline && (
            <div className="text-xs tracking-[0.28em] uppercase text-gray-300">{smallline}</div>
          )}
          <p className="text-gray-300 text-base sm:text-lg leading-relaxed max-w-md">
            {subtitle}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to={to}
              onClick={onChoose}
              data-testid={testid}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-sm w-max"
              style={{ background: accent, color: accent === "#A855F7" ? "#fff" : "#04140f" }}
            >
              {ctaLabel}
              <ArrowRight size={16} />
            </Link>
            {secondaryLabel && (
              <Link
                to={secondaryTo || to}
                onClick={onChoose}
                data-testid={`${testid}-secondary`}
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border hairline text-sm text-white hover:bg-white/10 w-max"
              >
                {secondaryLabel}
              </Link>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
